/**
 * LockIcon Component
 * Displays a lock icon with optional tooltip for indicating locked/disabled fields
 */

import React, { useState, useRef, useEffect } from 'react';
import { theme } from '../../theme';

export interface LockIconProps {
  /** Size of the icon in pixels */
  size?: number;
  /** Color of the icon */
  color?: string;
  /** Tooltip text to display on hover */
  tooltip?: string;
  /** Whether to show the tooltip on the left side */
  tooltipPosition?: 'left' | 'right' | 'top' | 'bottom';
  /** Additional className */
  className?: string;
  /** Additional styles */
  style?: React.CSSProperties;
  /** Click handler */
  onClick?: () => void;
}

/**
 * Lock icon component for indicating non-editable fields
 *
 * @example
 * ```tsx
 * <LockIcon
 *   tooltip="You don't have permission to edit this field"
 *   size={16}
 * />
 * ```
 */
export const LockIcon: React.FC<LockIconProps> = ({
  size = 16,
  color = theme.colors.text.tertiary,
  tooltip,
  tooltipPosition = 'top',
  className,
  style,
  onClick,
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipCoords, setTooltipCoords] = useState({ x: 0, y: 0 });
  const iconRef = useRef<HTMLSpanElement>(null);

  const handleMouseEnter = () => {
    if (tooltip && iconRef.current) {
      const rect = iconRef.current.getBoundingClientRect();
      setTooltipCoords({
        x: rect.left + rect.width / 2,
        y: rect.top,
      });
      setShowTooltip(true);
    }
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  const containerStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: tooltip ? 'help' : onClick ? 'pointer' : 'default',
    position: 'relative',
    ...style,
  };

  const tooltipStyle: React.CSSProperties = {
    position: 'fixed',
    zIndex: 10000,
    backgroundColor: theme.colors.background.elevated,
    color: theme.colors.text.primary,
    padding: '8px 12px',
    borderRadius: theme.borders.radius.md,
    fontSize: theme.typography.fontSize.sm,
    fontFamily: theme.typography.fontFamily.primary,
    maxWidth: '250px',
    boxShadow: theme.shadows.lg,
    border: `1px solid ${theme.colors.border.light}`,
    pointerEvents: 'none',
    whiteSpace: 'normal',
    wordWrap: 'break-word',
    lineHeight: 1.4,
    ...(tooltipPosition === 'top' && {
      bottom: `calc(100vh - ${tooltipCoords.y}px + 8px)`,
      left: tooltipCoords.x,
      transform: 'translateX(-50%)',
    }),
    ...(tooltipPosition === 'bottom' && {
      top: tooltipCoords.y + size + 8,
      left: tooltipCoords.x,
      transform: 'translateX(-50%)',
    }),
    ...(tooltipPosition === 'left' && {
      top: tooltipCoords.y - 4,
      right: `calc(100vw - ${tooltipCoords.x}px + ${size / 2 + 8}px)`,
    }),
    ...(tooltipPosition === 'right' && {
      top: tooltipCoords.y - 4,
      left: tooltipCoords.x + size / 2 + 8,
    }),
  };

  return (
    <span
      ref={iconRef}
      className={className}
      style={containerStyle}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={onClick}
      role={tooltip ? 'tooltip' : undefined}
      aria-label={tooltip}
    >
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        style={{ color }}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
      </svg>
      {showTooltip && tooltip && <div style={tooltipStyle}>{tooltip}</div>}
    </span>
  );
};

export default LockIcon;
